import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, ShoppingBag, X } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { useCartStore } from '../store/cartStore';
import { useCurrencyStore } from '../store/currencyStore';
import api from '../api/axios';
import toast from 'react-hot-toast';

const WishlistPage: React.FC = () => {
  const [wishlist, setWishlist] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { isAuthenticated } = useAuthStore();
  const { addToCart } = useCartStore();
  const { format } = useCurrencyStore();

  useEffect(() => {
    if (isAuthenticated) fetchWishlist();
    else setIsLoading(false);
  }, [isAuthenticated]);

  const fetchWishlist = async () => {
    try {
      setIsLoading(true);
      const res = await api.get('/api/users/wishlist');
      if (res.data.success) setWishlist(res.data.data || []);
    } catch {
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemove = async (productId: string) => {
    try {
      await api.delete(`/api/users/wishlist/${productId}`);
      setWishlist((prev) => prev.filter((item) => item.product_id !== productId));
      toast.success('Removed from wishlist');
    } catch {
      toast.error('Failed to remove item');
    }
  };

  const handleAddToCart = async (item: any) => {
    try {
      await addToCart(item.product_id, item.product_id, 1);
      toast.success('Added to bag');
    } catch {
      toast.error('Please select options on the product page');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="text-center space-y-6">
          <Heart size={40} className="text-nude mx-auto" />
          <h1 className="font-serif text-3xl text-primary">Your Wishlist</h1>
          <p className="text-xs tracking-widest uppercase text-nude">
            Sign in to view your saved items
          </p>
          <Link to="/login" className="btn-primary block max-w-xs mx-auto">
            Sign In
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-12 animate-fade-in">
      <div className="mb-12">
        <p className="text-xs tracking-widest uppercase text-nude mb-2">
          Saved Items
        </p>
        <h1 className="font-serif text-4xl text-primary">
          My Wishlist
          {wishlist.length > 0 && (
            <span className="text-nude font-sans text-sm ml-3">
              ({wishlist.length})
            </span>
          )}
        </h1>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="skeleton aspect-[3/4] rounded" />
          ))}
        </div>
      ) : wishlist.length === 0 ? (
        <div className="text-center py-24 space-y-6">
          <Heart size={48} className="text-nude mx-auto" />
          <h2 className="font-serif text-2xl text-primary">
            No saved items yet
          </h2>
          <p className="text-xs tracking-widest uppercase text-nude">
            Browse our collection and save your favourites
          </p>
          <Link to="/shop" className="btn-primary inline-block">
            Explore Collection
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {wishlist.map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className="group"
            >
              <div className="relative aspect-[3/4] bg-light overflow-hidden">
                {item.image_url ? (
                  <img
                    src={item.image_url}
                    alt={item.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-light to-nude/20" />
                )}

                {/* Remove Button */}
                <button
                  onClick={() => handleRemove(item.product_id)}
                  className="absolute top-3 right-3 p-2 bg-white rounded-full shadow-sm hover:bg-red-50 transition-colors"
                >
                  <X size={12} className="text-primary" />
                </button>

                {/* Add to Bag */}
                <motion.div
                  initial={{ opacity: 0 }}
                  whileHover={{ opacity: 1 }}
                  className="absolute bottom-0 left-0 right-0 p-3"
                >
                  <Link
                    to={`/products/${item.slug}`}
                    className="flex items-center justify-center space-x-2 bg-white text-primary text-xs tracking-widest uppercase py-3 hover:bg-primary hover:text-white transition-colors w-full"
                  >
                    <ShoppingBag size={12} />
                    <span>View Product</span>
                  </Link>
                </motion.div>
              </div>

              <div className="pt-3 space-y-1">
                <Link
                  to={`/products/${item.slug}`}
                  className="font-serif text-base text-primary hover:text-nude transition-colors block"
                >
                  {item.name}
                </Link>
                <p className="text-sm text-primary">
                  {format(Number(item.price))}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default WishlistPage;